<template>
  <div class="rd-screen">
    <report-state
      v-if="!selectedCompany"
      icon="mdi-domain"
      :title="$t('report_mobile.employees_pick_company')"
    ></report-state>
    <report-state v-else-if="loading && !report" kind="loading"></report-state>

    <!-- Panel iz "Preuzeto": samo lista radnika, radnik se otvara kao sledeći panel -->
    <div v-else-if="embedded" class="rd-list">
      <div class="rd-stats">
        <div class="rd-pill-stat">
          <span>{{ $t("ordered") }}</span>
          <b class="rd-num">{{ stats.ordered }}</b>
        </div>
        <div class="rd-pill-stat">
          <span>{{ $t("taken") }}</span>
          <b class="rd-num">{{ stats.taken }}</b>
        </div>
        <div class="rd-pill-stat rd-pill-stat--accent">
          <span>{{ $t("difference") }}</span>
          <b class="rd-num">{{ stats.difference }}</b>
        </div>
      </div>

      <ui-segment v-model="tab" tabs block :options="tabs.map((item) => ({ value: item.key, label: item.label }))" :label="$t('order_status')" />

      <div v-if="matchedWorkers.length" class="ui-card rd-card rd-rows" role="list" @keydown="onRowsKey">
        <button
          v-for="worker in matchedWorkers"
          :key="worker.id"
          type="button"
          class="rd-row"
          role="listitem"
          :aria-pressed="String(selectedId === worker.id)"
          :title="worker.source.external_code ? `${worker.name} · ${worker.source.external_code}` : worker.name"
          @click="openWorker(worker, $event)"
        >
          <span class="ui-avatar ui-avatar--neutral">{{ worker.initials }}</span>
          <span class="rd-row__text">
            <span class="rd-row__name">{{ worker.name }}</span>
            <span class="rd-row__meta" :title="worker.meta">{{ worker.meta }}</span>
          </span>
          <span class="rd-status" :class="`rd-status--${worker.status}`">{{ worker.statusLabel }}</span>
        </button>
      </div>
      <report-state v-else icon="mdi-account-off-outline" :title="embeddedEmptyText"></report-state>
    </div>

    <!-- Ceo spisak firme: ko je poručio, u kojoj smeni, ko je preuzeo. Klik na red otvara mesec radnika u panelu. -->
    <template v-else>
      <report-tools :copy="rows.length > 0" @copy="copyTable">
        <ui-chips v-model="orderStatus" :options="statusChips" :label="$t('order_status')" />
      </report-tools>

      <report-state v-if="!rows.length" icon="mdi-account-off-outline" :title="emptyText"></report-state>

      <ui-table
        v-else
        variant="report"
        wrap-class="rd-card--scroll"
        table-class="rd-tbl--workers"
        :columns="columns"
        :rows="rows"
        :sort="sort"
        :totals="totals"
        :total-label="$t('total')"
        :selected="isOpen"
        :row-class="workerRowClass"
        @sort="sortBy"
        @open="openWorker"
      >
        <template #head-shift="{ column }">
          <report-shift-tag :shift="column.shift.id"></report-shift-tag>
        </template>

        <template #cell-name="{ row }">
          <span class="rd-person">
            <span class="ui-avatar ui-avatar--neutral ui-avatar--sm">{{ row.initials }}</span>
            <span class="rd-person__name">{{ row.name }}</span>
          </span>
        </template>

        <template #cell-shift="{ row, column }">
          <span v-if="row[column.key]" class="rs-tag rs-tag--count" :data-shift="column.shift.id">
            {{ row[column.key] }}
            <span class="rs-tag__sr">{{ $t("report_mobile.shift_n", { n: column.shift.id }) }}</span>
          </span>
          <template v-else>
            <span class="rd-dash" aria-hidden="true">–</span>
            <span class="rd-visually-hidden">0</span>
          </template>
        </template>

        <template #cell-status="{ row }">
          <span class="rd-status" :class="`rd-status--${row.state}`">{{ row.statusLabel }}</span>
        </template>
      </ui-table>
    </template>
  </div>
</template>

<script>
import ReportMealsTabEmployees from "@/components/report/meals/tabs/ReportMealsTabEmployees.vue";
import moment from "moment";
import { SHIFTS } from "@/constants/shifts";
import hasWorkerList from "../../shared/hasWorkerList";
import ReportShiftTag from "../../shared/ReportShiftTag.vue";
import { copyText, rowsToTsv } from "../copyTable";
import hasTableSort from "../hasTableSort";
import ReportState from "../ReportState.vue";
import ReportTools from "../ReportTools.vue";
import ReportDesktopWorkerMonth from "./ReportDesktopWorkerMonth.vue";

// Šta je hitnije u sortiranju po statusu: nije preuzeo → deo → nije poručio → preuzeo.
const STATE_RANK = { none: 3, part: 2, off: 1, all: 0 };

/**
 * Radnici na računaru: spisak svih radnika firme sa smenama u kojima su poručili, poručeno / preuzeto /
 * razlikom i statusom (Nije poručio, Nije preuzeo, deo, Preuzeo). Filter statusa su čipovi sa brojevima.
 * Klik na red otvara mesečni pregled radnika u panelu. U panelu (iz "Preuzeto") je samo lista radnika.
 * Nasleđuje učitavanje, filter statusa, štampu i Excel iz ReportMealsTabEmployees (štampa i Excel prate
 * izabrani status i pretragu).
 */
export default {
  name: "ReportDesktopEmployees",
  extends: ReportMealsTabEmployees,
  mixins: [hasWorkerList, hasTableSort],
  components: { ReportShiftTag, ReportState, ReportTools },
  inject: {
    shell: { from: "reportShell", default: null },
  },
  props: {
    /** Smene iz trake: ne utiču na radnike (preuzeto se ne razlaže po smeni), samo se prihvataju */
    shifts: {
      type: Array,
      default: () => [],
    },
    /** Firma koja zamenjuje izabranu firmu iz store-a (panel iz "Preuzeto") */
    companyOverride: {
      type: Object,
      default: null,
    },
    /** Panel: samo lista radnika */
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    // Štampa/Excel nasleđeni iz starog taba: sve četiri smene kao kolone.
    selectedShifts: [1, 2, 3, 4],
    selectedId: null,
    sort: { key: "name", dir: 1 },
  }),
  computed: {
    /** Kolone tabele: iste služe za zaglavlje, redove i zbirni red. */
    columns() {
      return [
        { key: "id", label: "ID", cls: "rd-col-id", num: true, muted: true, sortable: true },
        { key: "name", label: this.$t("worker"), name: true, title: (row) => row.title, sortable: true },
        ...(this.isSingleDay
          ? [{ key: "time", label: this.$t("time"), cls: "rd-col-time", headTitle: this.$t("order_datetime"), num: true, muted: true, sortKey: "sortTime", sortable: true, value: (row) => row.time || "–" }]
          : []),
        ...this.visibleShifts.map((shift) => ({ key: shift.key, slot: "shift", shift, align: "center", sortable: true })),
        { key: "ordered", label: this.$t("ordered"), align: "end", num: true, zero: true, sortable: true },
        { key: "taken", label: this.$t("taken"), cls: "rd-col-taken", align: "end", num: true, zero: true, sortable: true },
        {
          key: "difference",
          label: this.$t("difference"),
          align: "end",
          num: true,
          zero: (row) => row.difference === 0,
          sortable: true,
          cellClass: (row) => ({ "is-alert": row.difference > 0 }),
        },
        { key: "status", label: this.$t("status"), sortKey: "stateRank", sortable: true },
        { key: "go", label: "", go: true },
      ];
    },
    selectedCompany() {
      return this.companyOverride || this.$store.state.report.selectedMealCompany;
    },

    // ---------------------------------------------------------- ceo spisak
    /** Čipovi statusa sa brojevima; vrednosti su isti statusi kao u starom izborniku (orderStatus). */
    statusChips() {
      const users = Array.isArray(this.report?.users) ? this.report.users : [];
      const ordered = users.filter((user) => (user.order_count || 0) > 0);
      const taken = ordered.filter((user) => (user.order_payed || 0) > 0).length;
      const counts = [users.length, ordered.length, users.length - ordered.length, taken, ordered.length - taken];
      return [
        { value: 0, label: this.$t("all") },
        { value: 1, label: this.$t("users_ordered") },
        { value: 2, label: this.$t("not_ordered") },
        { value: 3, label: this.$t("users_ordered_taken") },
        { value: 4, label: this.$t("users_ordered_not_taken") },
      ].map((chip, index) => ({ ...chip, count: counts[index] }));
    },
    /** Smene 1–3 uvek, četvrta samo kad neko u njoj ima narudžbu. */
    visibleShifts() {
      return SHIFTS.filter(
        (shift) => shift.id < 4 || this.users.some((user) => Number(user[shift.key]) > 0)
      );
    },
    allRows() {
      return this.users.map((user) => this.toRow(user));
    },
    rows() {
      const search = this.filters.search;
      const matched = search
        ? this.customSearch(
            this.allRows,
            [{ value: "name" }, { value: "idText" }, { value: "externalCode" }],
            search
          )
        : this.allRows;
      // abecedno kao druga mera: redovi sa istom vrednošću ostaju po imenu
      const byName = [...matched].sort((a, b) => a.name.localeCompare(b.name));
      return this.sortRows(byName);
    },
    totals() {
      const totals = { ordered: 0, taken: 0, difference: 0 };
      SHIFTS.forEach((shift) => {
        totals[shift.key] = 0;
      });
      this.rows.forEach((row) => {
        totals.ordered += row.ordered;
        totals.taken += row.taken;
        totals.difference += row.difference;
        SHIFTS.forEach((shift) => {
          totals[shift.key] += row[shift.key];
        });
      });
      return totals;
    },
    kpis() {
      return [
        { key: "ordered", label: this.$t("ordered"), value: this.totals.ordered, tone: "inverse" },
        { key: "taken", label: this.$t("taken"), value: this.totals.taken },
        { key: "difference", label: this.$t("difference"), value: this.totals.difference },
      ];
    },
    emptyText() {
      if (this.filters.search) {
        return this.$t("report_mobile.no_results_for", { search: this.filters.search });
      }
      return this.report?.users?.length
        ? this.$t("report_mobile.no_workers_in_view")
        : this.$t("no_employee_report_data");
    },

    // ---------------------------------------------------------- panel iz "Preuzeto"
    embeddedEmptyText() {
      if (this.filters.search) {
        return this.$t("report_mobile.no_results_for", { search: this.filters.search });
      }
      return this.workers.length
        ? this.$t("report_mobile.no_workers_in_view")
        : this.$t("no_employee_report_data");
    },
  },
  watch: {
    selectedCompany() {
      this.selectedId = null;
    },
    kpis: {
      handler(kpis) {
        this.$emit("kpis", !this.embedded && this.rows.length ? kpis : []);
      },
      immediate: true,
    },
    loading(loading) {
      this.$emit("loading", loading);
    },
  },
  methods: {
    workerRowClass(row) {
      return { "is-idle": row.state === "off" };
    },
    /** Red tabele: radnik iz starog taba (users) + stanje narudžbe i preuzimanja. */
    toRow(user) {
      const ordered = user.order_count || 0;
      const taken = user.order_payed || 0;
      const state = ordered <= 0 ? "off" : taken <= 0 ? "none" : taken >= ordered ? "all" : "part";
      const stamp = ordered > 0 && user.date ? moment(user.date, "DD.MM.YYYY HH:mm") : null;
      const name = user.full_name.trim();
      const row = {
        id: user.id,
        idText: String(user.id),
        externalCode: user.external_code || "",
        name,
        title: user.external_code ? `${name} · ${user.external_code}` : name,
        initials: [user.name, user.lastname]
          .filter(Boolean)
          .map((part) => part.charAt(0))
          .join(""),
        time: stamp && stamp.isValid() ? stamp.format("DD.MM. HH:mm") : "",
        sortTime: stamp && stamp.isValid() ? stamp.valueOf() : 0,
        ordered,
        taken,
        difference: user.order_difference,
        state,
        stateRank: STATE_RANK[state],
        statusLabel: state === "off" ? this.$t("report_desktop.status_not_ordered") : this.statusLabel(state, taken, ordered),
        source: user,
      };
      SHIFTS.forEach((shift) => {
        row[shift.key] = Number(user[shift.key]) || 0;
      });
      return row;
    },
    isOpen(row) {
      const top = this.shell?.panelStack?.[0];
      return Boolean(top && top.key === `worker-${row.id}`);
    },
    /** Mesečni pregled radnika u panelu (iz panela "Preuzeto" kao sledeći panel). */
    openWorker(worker, event) {
      this.selectedId = worker.id;
      const employee = this.toEmployeeContext(worker);
      this.$emit("open-panel", {
        opener: event?.currentTarget,
        key: `worker-${worker.id}`,
        root: !this.embedded,
        wide: true,
        title: worker.name,
        subtitle: [`ID ${worker.id}`, employee.companyName].filter(Boolean).join(" · "),
        component: ReportDesktopWorkerMonth,
        props: { worker: employee, compact: true },
      });
    },
    /** Strelice gore/dole prelaze između radnika u listi (panel). */
    onRowsKey(event) {
      if (!["ArrowDown", "ArrowUp"].includes(event.key)) {
        return;
      }
      const rows = [...event.currentTarget.querySelectorAll(".rd-row")];
      const index = rows.indexOf(document.activeElement);
      const next = rows[index + (event.key === "ArrowDown" ? 1 : -1)];
      if (next) {
        event.preventDefault();
        next.focus();
      }
    },
    async copyTable() {
      const headers = [
        "ID",
        this.$t("worker"),
        ...(this.isSingleDay ? [this.$t("time")] : []),
        ...this.visibleShifts.map((shift) => this.$t(shift.label)),
        this.$t("ordered"),
        this.$t("taken"),
        this.$t("difference"),
        this.$t("status"),
      ];
      const rows = this.rows.map((row) => [
        row.id,
        row.name,
        ...(this.isSingleDay ? [row.time] : []),
        ...this.visibleShifts.map((shift) => row[shift.key]),
        row.ordered,
        row.taken,
        row.difference,
        row.statusLabel,
      ]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
